/**
 * Helper functions for geocoding and coordinate operations
 */

/**
 * Convert address string to coordinates using Google Geocoding API
 * @param {string} address - Address string to geocode
 * @returns {Promise<{lat: number, lng: number}>} - Coordinates object with lat and lng
 */
export const geocodeAddress = async (address) => {
  if (!window.google || !window.google.maps) {
    throw new Error('Google Maps API not loaded');
  }
  
  const geocoder = new window.google.maps.Geocoder();
  
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng()
        });
      } else {
        reject(new Error(`Geocoding failed: ${status}`));
      }
    });
  });
};

/**
 * Parse PostgreSQL point coordinates to lat/lng object
 * @param {string|Object} point - PostgreSQL point as string "(lng,lat)" or object
 * @returns {{lat: number, lng: number}|null} - Parsed coordinates or null if invalid
 */
export const parsePointCoordinates = (point) => {
  if (!point) return null;
  
  console.log('Parsing coordinates:', point, typeof point);
  
  try {
    // Handle null/undefined explicitly
    if (point === null || point === 'null') {
      console.log('Null coordinates detected');
      return null;
    }
    
    // Handle string format "(lng,lat)"
    if (typeof point === 'string') {
      // Try PostgreSQL/PostGIS point format (lng,lat) or (x,y)
      const matches = point.match(/\((.+),(.+)\)/);
      if (matches && matches.length === 3) {
        // PostgreSQL POINT is stored as (x,y) which is (lng,lat) in PostGIS
        const result = {
          lng: parseFloat(matches[1]), // First value is longitude (x) in PostGIS
          lat: parseFloat(matches[2])  // Second value is latitude (y) in PostGIS
        };
        console.log('Parsed PostgreSQL point coordinates:', result);
        return result;
      }
      
      // Try comma-separated string "lat,lng"
      const parts = point.split(',');
      if (parts.length === 2) {
        const result = {
          lat: parseFloat(parts[0].trim()),
          lng: parseFloat(parts[1].trim())
        };
        console.log('Parsed comma-separated coordinates:', result);
        return result;
      }
    } 
    // Handle object format with lat/lng
    else if (typeof point === 'object') {
      // Handle Google Maps LatLng object
      if (typeof point.lat === 'function' && typeof point.lng === 'function') {
        const result = {
          lat: point.lat(),
          lng: point.lng()
        };
        console.log('Parsed Google LatLng object:', result);
        return result;
      }
      // Handle regular object with lat/lng properties
      else if (point.lat !== undefined && point.lng !== undefined) {
        const result = {
          lat: parseFloat(point.lat),
          lng: parseFloat(point.lng)
        };
        console.log('Parsed object with lat/lng:', result);
        return result;
      }
      // Handle PostGIS point object (x=lng, y=lat)
      else if (point.x !== undefined && point.y !== undefined) {
        const result = {
          lat: parseFloat(point.y),
          lng: parseFloat(point.x)
        };
        console.log('Parsed PostGIS point:', result);
        return result;
      }
    }
    
    // If we got here, we couldn't parse the coordinates
    console.warn('Could not parse coordinates format:', point);
  } catch (error) {
    console.error('Error parsing point coordinates:', error, point);
  }
  
  return null;
};

/**
 * Convert lat/lng object to PostgreSQL point format for database storage
 * @param {{lat: number, lng: number}} coordinates - Coordinates object 
 * @returns {string} - PostgreSQL point format "(lng,lat)"
 */
export const formatPointForStorage = (coordinates) => {
  if (!coordinates || !coordinates.lat || !coordinates.lng) {
    return null;
  }
  
  return `(${coordinates.lng},${coordinates.lat})`;
};

/**
 * Calculate distance between two points using Haversine formula
 * @param {{lat: number, lng: number}} point1 - First point coordinates
 * @param {{lat: number, lng: number}} point2 - Second point coordinates
 * @returns {number} - Distance in kilometers
 */
export const calculateDistance = (point1, point2) => {
  const R = 6371; // Radius of the Earth in km
  const dLat = deg2rad(point2.lat - point1.lat);
  const dLng = deg2rad(point2.lng - point1.lng);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(point1.lat)) * Math.cos(deg2rad(point2.lat)) * 
    Math.sin(dLng/2) * Math.sin(dLng/2); 
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const distance = R * c; // Distance in km
  
  return distance;
};

const deg2rad = (deg) => {
  return deg * (Math.PI/180);
};